import { useState } from 'react';
import { T, useGT } from 'gt-react';
import type {
  MemoryModule,
  MemoryProposalPayload,
  MemoryRuleAddress,
} from '@erp/shared-types';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useHarnessActions,
  useRouteTargets,
} from '@/api/rpc/hooks/harness/useHarness';
import {
  AliasField,
  CarrierTermFields,
  CustomerField,
  ExtractionHintFields,
  NoteField,
  OwnSiteFields,
  SenderRouteFields,
} from './proposeMemoryFields';
import { MemoryModuleSelect } from './MemoryModuleSelect';
import {
  DEFAULT_PROPOSE_MEMORY_MODULE,
  buildProposeMemoryPayload,
  effectiveProposeMemoryModule,
  initialProposeMemoryValues,
  proposeMemoryModuleOptions,
  knownDestinationAddress,
  proposalLabelName,
  proposeMemoryTitle,
  type ProposeMemoryDocumentScope,
  type ProposeMemoryInput,
  type ProposeMemoryMode as Mode,
  type ProposeMemoryRoutingFields,
} from './proposeMemoryDraft';

export interface ProposeMemoryFormProps {
  /** The draft the memory is proposed from; omitted when teaching from settings. */
  draftId?: string;
  customerId?: string | null;
  customerName?: string | null;
  extractedNames?: string[];
  extractedShippingText?: string | null;
  destinationAddress?: MemoryRuleAddress | null;
  /** Let the person pick the customer (settings) instead of inheriting the draft's. */
  allowCustomerPick?: boolean;
  /** Let the person type the address (settings) instead of using the draft's ship-to. */
  allowAddressEntry?: boolean;
  /** Which kind the form opens on (default: free-text note). */
  initialMode?: Mode;
  /** Hide the kind picker and keep the form on `initialMode`. */
  lockMode?: boolean;
  /** Pre-filled sender for a sender route (the email's From header). */
  initialSender?: string;
  initialDocumentScope?: ProposeMemoryDocumentScope;
  /**
   * Activate on submit instead of queueing a suggestion. The server requires
   * Approve on MemoryProposal; callers only pass it when the person has it.
   */
  acceptImmediately?: boolean;
  onSubmitted?: () => void;
  /** Renders the action row; `submit` is a no-op while the form is incomplete. */
  footer: (state: {
    submit: () => void;
    ready: boolean;
    pending: boolean;
  }) => React.ReactNode;
}

/** Form state plus the derived payload and the submit action. */
function useProposeMemoryDraft(props: ProposeMemoryFormProps) {
  const t = useGT();
  const actions = useHarnessActions(props.draftId);
  const initial = initialProposeMemoryValues({
    mode: props.initialMode,
    customerId: props.customerId,
    extractedNames: props.extractedNames,
    extractedShippingText: props.extractedShippingText,
    destinationAddress: props.destinationAddress,
    sender: props.initialSender,
    documentScope: props.initialDocumentScope,
  });
  const [mode, setMode] = useState<Mode>(initial.mode);
  const [pickedModule, setModule] = useState<MemoryModule>(
    DEFAULT_PROPOSE_MEMORY_MODULE,
  );
  const memoryModule = effectiveProposeMemoryModule(mode, pickedModule);
  const [customerId, setCustomerId] = useState<string | null>(
    initial.customerId,
  );
  const [text, setText] = useState('');
  const [alias, setAlias] = useState(initial.alias);
  const [term, setTerm] = useState(initial.term);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [address, setAddress] = useState<MemoryRuleAddress>(initial.address);
  const [routing, setRouting] = useState<ProposeMemoryRoutingFields>(
    initial.routing,
  );
  const patchRouting = (patch: Partial<ProposeMemoryRoutingFields>) =>
    setRouting((prev) => ({ ...prev, ...patch }));
  const routeTargets = useRouteTargets(mode === 'sender_route');
  const targets = routeTargets.data?.organizations ?? [];
  const customerName =
    customerId === initial.customerId ? (props.customerName ?? null) : null;

  const input: ProposeMemoryInput = {
    mode,
    customerId,
    text,
    alias,
    term,
    code,
    label,
    destinationAddress: knownDestinationAddress({
      allowAddressEntry: props.allowAddressEntry,
      address,
      destinationAddress: props.destinationAddress,
    }),
    routing,
    limitHintToCustomer: props.allowCustomerPick ?? false,
  };
  const payload = buildProposeMemoryPayload(input);
  const pending = actions.createProposal.isPending;

  async function submit() {
    if (!payload || pending) return;
    const sent = await sendProposal(t, {
      createProposal: actions.createProposal,
      payload,
      module: memoryModule,
      customerName,
      routeTargets: targets,
      draftId: props.draftId,
      acceptImmediately: props.acceptImmediately,
    });
    if (!sent) return;
    setText('');
    setAlias('');
    setLabel('');
    patchRouting({ fieldPath: '' });
    props.onSubmitted?.();
  }

  return {
    mode,
    setMode,
    memoryModule,
    setMemoryModule: setModule,
    customerId,
    setCustomerId,
    customerName,
    text,
    setText,
    alias,
    setAlias,
    term,
    setTerm,
    code,
    setCode,
    label,
    setLabel,
    address,
    setAddress,
    routing,
    patchRouting,
    targets,
    ready: payload !== null,
    pending,
    submit,
  };
}

/**
 * Sends the proposal (activating it when asked) and reports the outcome in
 * a toast. True when the server accepted it, so the form can reset.
 * `t` is positional: the i18n generator only follows the translator through
 * call arguments, not object properties, when extracting strings.
 */
async function sendProposal(
  t: ReturnType<typeof useGT>,
  params: {
    createProposal: ReturnType<typeof useHarnessActions>['createProposal'];
    payload: MemoryProposalPayload;
    module: MemoryModule;
    customerName: string | null;
    routeTargets: ReadonlyArray<{ id: string; name: string }>;
    draftId: string | undefined;
    acceptImmediately: boolean | undefined;
  },
): Promise<boolean> {
  const { payload, draftId, acceptImmediately } = params;
  try {
    await params.createProposal.mutateAsync({
      payload,
      module: params.module,
      title: proposeMemoryTitle(
        payload,
        proposalLabelName(payload, params.customerName, params.routeTargets),
        t,
      ),
      rationale: rationaleFor(draftId, t),
      evidence: draftId ? { draftSalesOrderIds: [draftId] } : undefined,
      acceptImmediately: acceptImmediately || undefined,
    });
    toast.success(
      acceptImmediately
        ? t('Memory saved and active', {
            context: 'toast after a memory was taught and activated',
          })
        : t('Sent to Proactive Suggestions for review', {
            context: 'toast after proposing a memory',
          }),
    );
    return true;
  } catch (error) {
    toast.error(error instanceof Error ? error.message : String(error));
    return false;
  }
}

function rationaleFor(
  draftId: string | undefined,
  t: ReturnType<typeof useGT>,
): string {
  return draftId
    ? t('Proposed from a sales-order draft by a reviewer.', {
        context: 'rationale for a proposal made from a draft',
      })
    : t('Taught directly by a reviewer in Org Settings.', {
        context: 'rationale for a memory taught from settings',
      });
}

function KindSelect({
  mode,
  onChange,
  aliasAllowed,
  ownSiteAllowed,
}: {
  mode: Mode;
  onChange: (mode: Mode) => void;
  aliasAllowed: boolean;
  ownSiteAllowed: boolean;
}) {
  const t = useGT();
  return (
    <div className="flex flex-col gap-1">
      <Label>
        <T context="label: kind of memory to propose">What kind of memory?</T>
      </Label>
      <Select value={mode} onValueChange={(v) => onChange(v as Mode)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="note">
            {t('Free-text note', { context: 'memory kind option' })}
          </SelectItem>
          <SelectItem value="customer_alias" disabled={!aliasAllowed}>
            {t('This name means this customer', {
              context: 'memory kind option',
            })}
          </SelectItem>
          <SelectItem value="carrier_term_alias">
            {t('This phrase means a shipping method', {
              context: 'memory kind option',
            })}
          </SelectItem>
          <SelectItem value="location_is_own_site" disabled={!ownSiteAllowed}>
            {t('The ship-to address is our own site', {
              context: 'memory kind option',
            })}
          </SelectItem>
          <SelectItem value="sender_route">
            {t('Documents from this sender belong to an organization', {
              context: 'memory kind option',
            })}
          </SelectItem>
          <SelectItem value="extraction_hint">
            {t('A hint for extracting a field', {
              context: 'memory kind option',
            })}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * The "teach the agent something" form shared by the draft's "Save as memory"
 * dialog and the Stored Memories › Teach tab. Everything becomes a proactive
 * suggestion (never a live rule); a reviewer approves it in Org Settings.
 *
 * Structured kinds cover the corrections we see most: a name that means a
 * customer, a carrier phrase that means a shipping method, an address that is
 * ours. Anything else is a free-text note the agent reads for context.
 */
export function ProposeMemoryForm(props: ProposeMemoryFormProps) {
  const form = useProposeMemoryDraft(props);
  const allowCustomerPick = props.allowCustomerPick ?? false;
  const allowAddressEntry = props.allowAddressEntry ?? false;
  const showCustomerPicker =
    allowCustomerPick &&
    form.mode !== 'location_is_own_site' &&
    form.mode !== 'sender_route';

  let fields: React.ReactNode = null;
  switch (form.mode) {
    case 'note':
      fields = <NoteField value={form.text} onChange={form.setText} />;
      break;
    case 'customer_alias':
      fields = (
        <AliasField
          alias={form.alias}
          onChange={form.setAlias}
          bindsTo={
            form.customerId ? (form.customerName ?? form.customerId) : null
          }
        />
      );
      break;
    case 'carrier_term_alias':
      fields = (
        <CarrierTermFields
          term={form.term}
          code={form.code}
          onTermChange={form.setTerm}
          onCodeChange={form.setCode}
        />
      );
      break;
    case 'location_is_own_site':
      fields = (
        <OwnSiteFields
          label={form.label}
          onLabelChange={form.setLabel}
          address={form.address}
          onAddressChange={form.setAddress}
          editableAddress={allowAddressEntry}
        />
      );
      break;
    case 'sender_route':
      fields = (
        <SenderRouteFields
          routing={form.routing}
          onRoutingChange={form.patchRouting}
          note={form.text}
          onNoteChange={form.setText}
          targets={form.targets}
        />
      );
      break;
    case 'extraction_hint':
      fields = (
        <ExtractionHintFields
          routing={form.routing}
          onRoutingChange={form.patchRouting}
          hint={form.text}
          onHintChange={form.setText}
        />
      );
      break;
  }

  return (
    <div className="flex flex-col gap-3 text-sm">
      {props.lockMode ? null : (
        <KindSelect
          mode={form.mode}
          onChange={form.setMode}
          aliasAllowed={!!form.customerId || allowCustomerPick}
          ownSiteAllowed={!!props.destinationAddress || allowAddressEntry}
        />
      )}
      <AreaField
        mode={form.mode}
        value={form.memoryModule}
        onChange={form.setMemoryModule}
      />
      {showCustomerPicker ? (
        <CustomerField
          required={form.mode === 'customer_alias'}
          customerId={form.customerId}
          onChange={form.setCustomerId}
          limitsHint={form.mode === 'extraction_hint'}
        />
      ) : null}
      {fields}
      {props.footer({
        submit: form.submit,
        ready: form.ready,
        pending: form.pending,
      })}
    </div>
  );
}

/** The area the memory is filed under; shown only when the kind has a choice. */
function AreaField({
  mode,
  value,
  onChange,
}: {
  mode: Mode;
  value: MemoryModule;
  onChange: (module: MemoryModule) => void;
}) {
  const t = useGT();
  const options = proposeMemoryModuleOptions(mode);
  if (options.length < 2) return null;
  return (
    <div className="flex flex-col gap-1">
      <Label>
        <T context="label: the memory area a taught memory is filed under">
          Area
        </T>
      </Label>
      <MemoryModuleSelect
        value={value}
        onChange={onChange}
        options={options}
        ariaLabel={t('Area', {
          context: 'the memory area a taught memory is filed under',
        })}
      />
    </div>
  );
}

/** The standard action row: a primary Propose button and an optional secondary. */
export function ProposeMemoryActions({
  submit,
  ready,
  pending,
  secondary,
  activates,
}: {
  submit: () => void;
  ready: boolean;
  pending: boolean;
  secondary?: React.ReactNode;
  /** The submit activates the memory right away rather than proposing it. */
  activates?: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-2 pt-1">
      {secondary}
      <Button disabled={!ready || pending} onClick={submit}>
        {activates ? (
          <T context="submit a memory that activates immediately">
            Save Memory
          </T>
        ) : (
          <T context="submit a memory proposal">Propose</T>
        )}
      </Button>
    </div>
  );
}
